import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { motion } from 'framer-motion'
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts'
import { 
  TrendingUp, 
  Users, 
  UserPlus, 
  BookOpen,
  Calendar,
  AlertCircle
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { RootState, AppDispatch } from '../../store'
import { fetchStudentStats } from '../../store/slices/studentSlice'

const StaffAnalytics = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { 
    stats: { totalActive, newThisMonth, courses, isLoadingStats },
    pagination: studentPagination 
  } = useSelector((state: RootState) => state.student)

  const [selectedTimeRange, setSelectedTimeRange] = useState('today')
  const [selectedGroup, setSelectedGroup] = useState('all')

  useEffect(() => {
    dispatch(fetchStudentStats())
  }, [dispatch])

  // Mock data for charts (in real app, this would come from API)
  const todaysEnrollmentData = [
    { hour: '9 AM', CSE: 2, IT: 1, ECE: 0, MECH: 1 },
    { hour: '10 AM', CSE: 4, IT: 2, ECE: 1, MECH: 0 },
    { hour: '11 AM', CSE: 3, IT: 1, ECE: 2, MECH: 1 },
    { hour: '12 PM', CSE: 5, IT: 3, ECE: 1, MECH: 2 },
    { hour: '1 PM', CSE: 2, IT: 1, ECE: 0, MECH: 0 },
    { hour: '2 PM', CSE: 6, IT: 4, ECE: 2, MECH: 1 },
    { hour: '3 PM', CSE: 4, IT: 2, ECE: 1, MECH: 1 },
    { hour: '4 PM', CSE: 3, IT: 1, ECE: 1, MECH: 0 },
  ]

  const weeklyTrends = [
    { day: 'Mon', enrollments: 12, verifications: 8, updates: 15 },
    { day: 'Tue', enrollments: 15, verifications: 12, updates: 18 },
    { day: 'Wed', enrollments: 18, verifications: 15, updates: 22 },
    { day: 'Thu', enrollments: 22, verifications: 18, updates: 25 },
    { day: 'Fri', enrollments: 20, verifications: 16, updates: 20 },
    { day: 'Sat', enrollments: 8, verifications: 5, updates: 10 },
    { day: 'Sun', enrollments: 5, verifications: 3, updates: 6 },
  ]

  const groupWiseData = courses.map(course => ({
    name: course.name,
    students: course.studentCount,
    newThisMonth: Math.floor(Math.random() * 10) + 1,
    completion: Math.floor(Math.random() * 20) + 80
  }))

  const yearWiseData = [
    { year: '1st Year', students: Math.floor(totalActive * 0.35), color: '#3B82F6' },
    { year: '2nd Year', students: Math.floor(totalActive * 0.28), color: '#10B981' },
    { year: '3rd Year', students: Math.floor(totalActive * 0.25), color: '#F59E0B' },
    { year: '4th Year', students: Math.floor(totalActive * 0.12), color: '#EF4444' },
  ]

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4']

  const analyticsMetrics = [
    {
      title: 'Today\'s Enrollments',
      value: '24',
      change: '+18%',
      trend: 'up',
      description: 'New students enrolled today',
      icon: UserPlus,
      color: 'text-blue-600'
    },
    {
      title: 'Documents Verified',
      value: '18',
      change: '+12%',
      trend: 'up',
      description: 'Academic certificates verified',
      icon: AlertCircle,
      color: 'text-green-600'
    },
    {
      title: 'Profile Updates',
      value: '32',
      change: '+5%',
      trend: 'up',
      description: 'Student profiles updated',
      icon: Users,
      color: 'text-orange-600'
    },
    {
      title: 'Pending Actions',
      value: '8',
      change: '-15%',
      trend: 'down',
      description: 'Tasks requiring attention',
      icon: Calendar,
      color: 'text-red-600'
    }
  ]

  if (isLoadingStats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-xl font-bold text-gray-800">Enrollment Analytics</h1>
          <p className="text-gray-600 text-sm">Student enrollment trends and insights</p>
        </div>
        <div className="flex items-center space-x-4">
          <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Time Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedGroup} onValueChange={setSelectedGroup}>
            <SelectTrigger>
              <SelectValue placeholder="Course Group" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Groups</SelectItem>
              {courses.map(course => (
                <SelectItem key={course.id} value={course.code}>
                  {course.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </motion.div>

      {/* Analytics Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {analyticsMetrics.map((metric, index) => {
          const Icon = metric.icon
          return (
            <motion.div
              key={metric.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-1">
                        {metric.title}
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        {metric.value}
                      </p>
                      <div className="flex items-center mt-2">
                        <TrendingUp className={`h-3 w-3 mr-1 ${
                          metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                        }`} />
                        <span className={`text-xs font-medium ${
                          metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {metric.change}
                        </span>
                      </div>
                    </div>
                    <div className={`w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center`}>
                      <Icon className={`h-6 w-6 ${metric.color}`} />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    {metric.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's CSE Enrollments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart className="h-5 w-5" />
                <span>Today's Enrollment Trends</span>
              </CardTitle>
              <CardDescription>
                Hourly enrollment distribution by course group
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={todaysEnrollmentData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="CSE" fill="#3B82F6" name="CSE" />
                    <Bar dataKey="IT" fill="#10B981" name="IT" />
                    <Bar dataKey="ECE" fill="#F59E0B" name="ECE" />
                    <Bar dataKey="MECH" fill="#EF4444" name="MECH" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Year-wise Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Users className="h-5 w-5" />
                <span>Year-wise Student Distribution</span>
              </CardTitle>
              <CardDescription>
                Current student distribution across academic years
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={yearWiseData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="students"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {yearWiseData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Activity Trends */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5" />
                <span>Weekly Activity Trends</span>
              </CardTitle>
              <CardDescription>
                Daily breakdown of enrollment activities
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="enrollments" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      name="New Enrollments"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="verifications" 
                      stroke="#10B981" 
                      strokeWidth={2}
                      name="Verifications"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="updates" 
                      stroke="#F59E0B" 
                      strokeWidth={2}
                      name="Profile Updates"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Group-wise Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BookOpen className="h-5 w-5" />
                <span>Course Group Performance</span>
              </CardTitle>
              <CardDescription>
                Enrollment and completion metrics by course group
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {groupWiseData.map((group, index) => (
                  <motion.div
                    key={group.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.1 + index * 0.1 }}
                    className="p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">{group.name}</h3>
                      <Badge 
                        variant="outline"
                        className={`${
                          group.completion >= 90 ? 'bg-green-100 text-green-800' :
                          group.completion >= 80 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}
                      >
                        {group.completion}% Complete
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Total Students:</span>
                        <span className="font-medium ml-2">{group.students}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">New This Month:</span>
                        <span className="font-medium ml-2 text-green-600">+{group.newThisMonth}</span>
                      </div>
                    </div>
                    <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          group.completion >= 90 ? 'bg-green-500' :
                          group.completion >= 80 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${group.completion}%` }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}

export default StaffAnalytics
